<script setup lang="ts">
import MinersConsolidationGrid from '@/components/rc/miner-repairs/consolidation/MinersConsolidationGrid.vue'
import { useMinerPartSwaps } from '@/composables/rc/miner-part-swaps'
import { type IConsolidationAction } from '@/types/rc/miners/forms'
import { Icon } from '@iconify/vue'
import { computed, ref } from 'vue'

const open = defineModel<boolean>()

const props = defineProps<{
  workOrder: string
  customerId: string
  preset1?: number
  preset2?: number
  preset3?: number
}>()

const preset = computed(() => [props.preset1, props.preset2, props.preset3])

// Model for the miners-consolidation-grid to fill in
const gridActions = ref<IConsolidationAction[]>([])

const gridRef = ref<InstanceType<typeof MinersConsolidationGrid>>()

const { movePartsMutation } = useMinerPartSwaps()

const { mutate: consolidateParts, isPending: movesPending } = movePartsMutation

// Delete undo actions info before posting to BE
const bePayload = computed(() => gridRef.value?.cleanActions.map((action) => ({ ...action, undo: undefined })))

// Hit BE
const saveChanges = async () => {
  if (!bePayload.value) return
  consolidateParts(bePayload.value, {
    onSuccess: () => {
      gridActions.value = []
    },
  })
}

const close = () => {
  gridRef.value?.reset()
  open.value = false
}
</script>

<template>
  <el-dialog v-model="open" :width="1440" class="mt-8" @closed="gridRef?.clear" @open="gridRef?.load">
    <template #header>
      <h3>Consolidation</h3>
      <p class="text-tertiary">{{ workOrder }}</p>
    </template>
    <div class="flex flex-col min-h-128">
      <MinersConsolidationGrid
        ref="gridRef"
        v-model="gridActions"
        :work-order-number="workOrder"
        :customer-id="customerId"
        :preset="preset"
      />
    </div>
    <template #footer>
      <div class="flex justify-between pt-2.5">
        <div class="flex flex-row gap-1">
          <el-button :disabled="gridActions.length == 0" @click="gridRef?.undoAction">
            <icon icon="mdi:undo-variant" class="mr-1" />
            <span>Undo</span>
          </el-button>
          <el-button :disabled="gridActions.length == 0" @click="gridRef?.reset">Reset</el-button>
        </div>
        <div class="flex flex-row gap-1">
          <el-button @click="close">Cancel</el-button>
          <el-button
            :loading="movesPending"
            type="primary"
            :disabled="gridRef?.cleanActions.length === 0"
            @click="saveChanges"
          >
            Save changes
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>
